import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize from 'rehype-sanitize';
import { wikiArticleApi } from '../utils/api';
import { API_ORIGIN, resolveApiUrl } from '../utils/apiBase';
import { useAuth } from '../lib/security';
import { Check, ChevronRight, Link as LinkIcon } from 'lucide-react';
import WikiDocsNav from './WikiDocsNav';
import AuthenticatedImage from './AuthenticatedImage';
import { useWikiDocs, findSectionPath } from '../utils/wikiToc';

interface WikiArticle {
    id: string;
    tocId: string;
    title: string;
    content: string;
    tags: string | null;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
    updatedBy: string | null;
    createdByName: string | null;
    updatedByName: string | null;
    isPublished: boolean;
    tocName?: string;
    parentTocName?: string;
}

/** Slug id for a heading, matching the ids the rendered headings carry. */
const generateId = (text: string): string =>
    text
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

/** Pull the heading outline out of raw Markdown, for the right-hand rail. */
const extractHeadings = (content: string): { level: number; text: string; id: string }[] => {
    const regex = /^(#{1,6})\s+(.+)$/gm;
    const found: { level: number; text: string; id: string }[] = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
        found.push({
            level: match[1].length,
            text: match[2].trim(),
            id: generateId(match[2].trim()),
        });
    }
    return found;
};

const WikiArticlePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { canWrite, canAdmin } = useAuth();
    const [article, setArticle] = useState<WikiArticle | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [headings, setHeadings] = useState<{ level: number; text: string; id: string }[]>([]);
    const [activeHeadingId, setActiveHeadingId] = useState<string | null>(null);
    const [showMobileToc, setShowMobileToc] = useState(false);
    const [copied, setCopied] = useState(false);

    // The section tree powers the left rail, so the reader keeps their place in
    // the wider area rather than having to go back to the landing page.
    const { sections, articles } = useWikiDocs();

    // Extract headings when article content changes.
    //
    // generateId and extractHeadings live at module scope deliberately. Defined
    // in the body they were rebuilt on every render, so the useCallback around
    // extractHeadings never held, this effect re-ran, and setHeadings stored a
    // fresh array each time - a new identity, so React could not bail out, and
    // the component re-rendered forever. That loop is what made the page
    // flicker, and it also tore down and rebuilt the scroll-spy observer below
    // on every pass.
    useEffect(() => {
        if (article?.content) {
            setHeadings(extractHeadings(article.content));
        }
    }, [article?.content]);

    // Scroll-spy: track which heading is currently in view
    useEffect(() => {
        if (headings.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveHeadingId(entry.target.id);
                    }
                });
            },
            { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
        );

        const elements = document.querySelectorAll('.markdown-content h1, .markdown-content h2, .markdown-content h3, .markdown-content h4, .markdown-content h5, .markdown-content h6');
        elements.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, [headings]);

    const scrollToHeading = (id: string) => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const fetchArticle = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        const result = await wikiArticleApi.getById(id);
        if (result.error) {
            setError(result.error);
            setArticle(null);
        } else {
            setArticle(result.data as WikiArticle);
        }
        setLoading(false);
    }, [id]);

    useEffect(() => {
        fetchArticle();
    }, [fetchArticle]);

    const handleCopyLink = async () => {
        if (!id) return;
        const url = `${window.location.origin}/wiki/article/${id}`;
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDelete = async () => {
        if (!id) return;
        const result = await wikiArticleApi.delete(id);
        if (result.error) {
            setError(result.error);
        } else {
            navigate('/wiki');
        }
    };

    // Convert markdown image syntax ![alt](path) to URLs on the API origin
    const fixImagePaths = (content: string): string => {
        return content.replace(
            /!\[([^\]]*)\]\(([^)]+)\)/g,
            (_match, alt, src) => {
                // If it's already a full URL, use it as-is
                if (src.startsWith('http')) return `![${alt}](${src})`;
                // Relative /api paths need rebasing onto the Functions app
                if (src.startsWith('/api')) return `![${alt}](${resolveApiUrl(src)})`;
                // Otherwise it's a bare blob name - build the proxy URL
                return `![${alt}](${API_ORIGIN}/api/images/${src})`;
            }
        );
    };

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-8 flex justify-center">
                <div className="spinner"></div>
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="text-center py-16">
                    <div className="text-4xl mb-4">📄</div>
                    <h2 className="text-xl font-semibold text-slate-700 mb-2">Article Not Found</h2>
                    <p className="text-slate-500 mb-4">{error || 'The requested article does not exist.'}</p>
                    <button
                        onClick={() => navigate('/wiki')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Back to Documentation
                    </button>
                </div>
            </div>
        );
    }

    // Generate IDs for headings and inject via custom component
    const headingIds = headings.reduce<Record<string, string>>((acc, h) => {
        acc[h.text] = h.id;
        return acc;
    }, {});

    const sectionPath = findSectionPath(sections, article.tocId);
    const rootSection = sectionPath[0];

    return (
    <div className="bx-wrap py-8 fade-in">

            {/* Section rail, article, and in-page contents */}
            <div className="flex gap-8">
                {rootSection && (
                    <WikiDocsNav
                        section={rootSection}
                        articles={articles}
                        activeId={article.id}
                        activeSectionId={article.tocId}
                    />
                )}

                {/* Article content */}
                <div className="flex-1 min-w-0">
                    {/* Article header */}
                    <div className="mb-8">
                        <nav className="flex items-center flex-wrap gap-1 text-sm text-slate-500 mb-3 no-print">
                            <Link to="/wiki" className="hover:text-brand-600">Documentation</Link>
                            {sectionPath.map(crumb => (
                                <React.Fragment key={crumb.id}>
                                    <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                                    <Link to={`/wiki/section/${crumb.id}`} className="hover:text-brand-600">
                                        {crumb.description}
                                    </Link>
                                </React.Fragment>
                            ))}
                        </nav>
                        <h1 className="text-3xl font-bold text-slate-800 mb-4 break-words">{article.title}</h1>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span>By {article.createdByName || article.createdBy || 'Unknown'}</span>
                            <span>•</span>
                            <span>Created {new Date(article.createdAt).toLocaleDateString()}</span>
                            <span>•</span>
                            <span>Updated {new Date(article.updatedAt).toLocaleDateString()}</span>
                            {article.tags && (
                                <>
                                    <span>•</span>
                                    <div className="flex gap-1">
                                        {article.tags.split(',').map((tag, i) => (
                                            <span key={i} className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded text-xs">
                                                {tag.trim()}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mb-6 no-print">
                        {canWrite('wiki') && (
                            <Link
                                to={`/wiki/edit/${article.id}`}
                                className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors font-medium"
                            >
                                ✏️ Edit
                            </Link>
                        )}
                        {canAdmin('wiki') && (
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors font-medium"
                            >
                                🗑️ Delete
                            </button>
                        )}
                        <button
                            onClick={handleCopyLink}
                            className="px-3 py-1.5 text-sm bg-slate-50 text-slate-600 rounded-md hover:bg-slate-100 transition-colors font-medium flex items-center gap-1.5"
                            title={copied ? 'Copied!' : 'Copy link to this article'}
                        >
                            {copied ? (
                                <Check className="w-4 h-4 text-green-600" />
                            ) : (
                                <LinkIcon className="w-4 h-4" />
                            )}
                            {copied ? 'Copied!' : 'Copy Link'}
                        </button>
                        <button
                            onClick={() => window.print()}
                            className="px-3 py-1.5 text-sm bg-slate-50 text-slate-600 rounded-md hover:bg-slate-100 transition-colors font-medium"
                        >
                            🖨️ Print
                        </button>
                    </div>

                    {/* Article content */}
                    <article className="markdown-content prose prose-slate max-w-none">
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeHighlight, rehypeSanitize]}
                            components={{
                                img: ({ src, alt }) => (
                                    <AuthenticatedImage src={src} alt={alt} className="max-w-full h-auto rounded-lg my-4" />
                                ),
                                h1: ({ children }) => {
                                    const text = React.Children.toArray(children).map(String).join('');
                                    const id = headingIds[text] ?? generateId(text);
                                    return <h1 id={id} className="scroll-mt-24">{children}</h1>;
                                },
                                h2: ({ children }) => {
                                    const text = React.Children.toArray(children).map(String).join('');
                                    const id = headingIds[text] ?? generateId(text);
                                    return <h2 id={id} className="scroll-mt-24">{children}</h2>;
                                },
                                h3: ({ children }) => {
                                    const text = React.Children.toArray(children).map(String).join('');
                                    const id = headingIds[text] ?? generateId(text);
                                    return <h3 id={id} className="scroll-mt-24">{children}</h3>;
                                },
                                h4: ({ children }) => {
                                    const text = React.Children.toArray(children).map(String).join('');
                                    const id = headingIds[text] ?? generateId(text);
                                    return <h4 id={id} className="scroll-mt-24">{children}</h4>;
                                },
                                h5: ({ children }) => {
                                    const text = React.Children.toArray(children).map(String).join('');
                                    const id = headingIds[text] ?? generateId(text);
                                    return <h5 id={id} className="scroll-mt-24">{children}</h5>;
                                },
                                h6: ({ children }) => {
                                    const text = React.Children.toArray(children).map(String).join('');
                                    const id = headingIds[text] ?? generateId(text);
                                    return <h6 id={id} className="scroll-mt-24">{children}</h6>;
                                },
                            }}
                        >
                            {fixImagePaths(article.content)}
                        </ReactMarkdown>
                    </article>
                </div>

                {/* Table of Contents sidebar */}
                {headings.length > 0 && (
                    <>
                        {/* Mobile TOC toggle */}
                        <button
                            onClick={() => setShowMobileToc(!showMobileToc)}
                            className="lg:hidden fixed bottom-6 right-6 z-40 px-4 py-2 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                            ☰ On this page
                        </button>

                        {/* Mobile TOC overlay */}
                        {showMobileToc && (
                            <div className="lg:hidden fixed inset-0 bg-black/40 z-50" onClick={() => setShowMobileToc(false)}>
                                <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-xl overflow-y-auto p-4">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                                            Table of Contents
                                        </h3>
                                        <button
                                            onClick={() => setShowMobileToc(false)}
                                            className="text-slate-400 hover:text-slate-600 text-xl"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                    <nav className="space-y-1 border-l-2 border-slate-200">
                                        {headings.map((heading) => (
                                            <button
                                                key={heading.id}
                                                onClick={() => {
                                                    scrollToHeading(heading.id);
                                                    setShowMobileToc(false);
                                                }}
                                                className={`block w-full text-left py-1 pl-3 text-sm transition-colors border-l-2 -ml-px ${
                                                    activeHeadingId === heading.id
                                                        ? 'border-blue-600 text-blue-600 font-medium'
                                                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                                } ${
                                                    heading.level === 2
                                                        ? ''
                                                        : heading.level === 3
                                                        ? 'pl-6'
                                                        : 'pl-9'
                                                }`}
                                            >
                                                {heading.text}
                                            </button>
                                        ))}
                                    </nav>
                                </div>
                            </div>
                        )}

                        {/* Desktop TOC */}
                        <aside className="hidden lg:block w-64 flex-shrink-0">
                        <div className="sticky top-8">
                            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                                Table of Contents
                            </h3>
                            <nav className="space-y-1 border-l-2 border-slate-200">
                                {headings.map((heading) => (
                                    <button
                                        key={heading.id}
                                        onClick={() => scrollToHeading(heading.id)}
                                        className={`block w-full text-left py-1 pl-3 text-sm transition-colors border-l-2 -ml-px ${
                                            activeHeadingId === heading.id
                                                ? 'border-blue-600 text-blue-600 font-medium'
                                                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                        } ${
                                            heading.level === 2
                                                ? ''
                                                : heading.level === 3
                                                ? 'pl-6'
                                                : 'pl-9'
                                        }`}
                                    >
                                        {heading.text}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </aside>
                    </>
                )}
            </div>

            {/* Delete confirmation modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 fade-in">
                        <h3 className="text-lg font-bold text-slate-800 mb-2">Delete Article</h3>
                        <p className="text-sm text-slate-600 mb-6">
                            Are you sure you want to delete <strong>{article.title}</strong>? This action cannot be undone.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WikiArticlePage;
